import React, { createContext, useCallback, useContext, useState } from "react";

interface AppError {
  message: string;
  error?: Error;
}

interface ErrorContextValue {
  /** Active backend/app error, or null when the app is healthy. */
  appError: AppError | null;
  /** Call from any API service or hook to surface the error page. */
  reportError: (message: string, error?: Error) => void;
  /** Dismiss the error and return to normal rendering. */
  clearError: () => void;
}

const ErrorContext = createContext<ErrorContextValue | null>(null);

export function ErrorProvider({ children }: { children: React.ReactNode }) {
  const [appError, setAppError] = useState<AppError | null>(null);

  const reportError = useCallback((message: string, error?: Error) => {
    console.error("[ErrorContext] Backend error reported:", message, error);
    setAppError({ message, error });
  }, []);

  const clearError = useCallback(() => setAppError(null), []);

  return (
    <ErrorContext.Provider value={{ appError, reportError, clearError }}>
      {children}
    </ErrorContext.Provider>
  );
}

/** Hook for any component or service to report or clear a backend error. */
export function useErrorReporter() {
  const ctx = useContext(ErrorContext);
  if (!ctx) throw new Error("useErrorReporter must be used inside <ErrorProvider>");
  return ctx;
}

/** Read-only hook — just reads the current appError state. */
export function useAppError() {
  const ctx = useContext(ErrorContext);
  if (!ctx) throw new Error("useAppError must be used inside <ErrorProvider>");
  return ctx.appError;
}
